import { useState } from 'react'
import dayjs from 'dayjs'
import { toast } from 'sonner'
import { X, Plus, ShieldCheck, Trash2 } from '@/components/icons'
import {
  Drawer,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from './ui/drawer'
import { Button } from './ui/button'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog'
import { EmptyState } from './EmptyState'
import { ListSkeleton } from './ui/skeleton-presets'
import { Pill } from './ui/pill'
import { getDocumentExpiryStatus } from './DocumentListCard'
import { useEquipmentGetByAircraft, useEquipmentDelete } from '../lib/hooks'
import { useIsAdmin } from '@/hooks/useIsCompanyAdmin'
import {
  AircraftEquipmentFormDrawer,
  type EquipmentItem,
} from './AircraftEquipmentFormDrawer'

interface AircraftEquipmentDrawerProps {
  aircraftId: string
  open: boolean
  onClose: () => void
}

function EquipmentExpiryPill({ expiryDate }: { expiryDate: EquipmentItem['expiryDate'] }) {
  if (!expiryDate) return <Pill variant="draft">No expiry</Pill>
  const label = dayjs.utc(expiryDate).format('MMM D YYYY')
  const status = getDocumentExpiryStatus(expiryDate)
  if (status === 'expired') return <Pill variant="error">Expired {label}</Pill>
  if (status === 'expiring') return <Pill variant="warning">Expires {label}</Pill>
  return <Pill variant="success">Expires {label}</Pill>
}

export function AircraftEquipmentDrawer({
  aircraftId,
  open,
  onClose,
}: AircraftEquipmentDrawerProps) {
  const { isAdmin } = useIsAdmin()
  const { data: items = [], isPending: loading } = useEquipmentGetByAircraft(aircraftId, {
    enabled: open,
  })
  const { mutate: deleteEquipment } = useEquipmentDelete(aircraftId)

  const [formOpen, setFormOpen] = useState(false)
  const [editingItem, setEditingItem] = useState<EquipmentItem | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<EquipmentItem | null>(null)

  const openCreate = () => {
    setEditingItem(null)
    setFormOpen(true)
  }

  // Non-admins open the form read-only to see serial, location and notes.
  const openItem = (item: EquipmentItem) => {
    setEditingItem(item)
    setFormOpen(true)
  }

  const handleDelete = () => {
    if (!deleteTarget) return
    deleteEquipment(
      { id: deleteTarget.id },
      {
        onSuccess: () => {
          toast.success('Equipment removed')
          setDeleteTarget(null)
        },
        onError: () => {
          toast.error('Failed to remove equipment')
          setDeleteTarget(null)
        },
      }
    )
  }

  return (
    <>
      <Drawer open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
        <DrawerContent className="bg-surface-L0 max-h-[85dvh]">
          <DrawerHeader className="flex items-center justify-between p-4">
            <DrawerTitle className="text-lg font-semibold text-content-primary-bold">
              Safety Equipment
            </DrawerTitle>
            <Button
              variant="secondary"
              size="icon"
              roundness="round"
              className="size-8"
              onClick={onClose}
            >
              <X className="w-4 h-4" />
            </Button>
          </DrawerHeader>

          {/* Content */}
          <div
            className="flex-1 overflow-y-auto px-4 pb-2"
            style={{ WebkitOverflowScrolling: 'touch' }}
          >
            {loading ? (
              <ListSkeleton />
            ) : items.length === 0 ? (
              <EmptyState
                icon={ShieldCheck}
                title="No equipment"
                description="Track safety equipment and its expiry dates. Admins are notified before items expire."
                addButtonText={isAdmin ? 'Add Equipment' : undefined}
                onAdd={isAdmin ? openCreate : undefined}
              />
            ) : (
              <div className="flex flex-col gap-2 py-2">
                {(items as EquipmentItem[]).map((item) => {
                  const subtitle = [
                    item.quantity > 1 ? `×${item.quantity}` : null,
                    item.location,
                    item.serialNumber ? `S/N ${item.serialNumber}` : null,
                  ]
                    .filter(Boolean)
                    .join(' · ')
                  return (
                    <div
                      key={item.id}
                      className="flex items-center gap-3 rounded-2xl border border-border-L1 bg-surface-L1 px-4 py-2"
                    >
                      <button
                        type="button"
                        onClick={() => openItem(item)}
                        className="flex flex-col items-start gap-1 flex-1 min-w-0 text-left"
                      >
                        <span className="py-1 text-sm font-medium text-content-primary-bold leading-5 truncate max-w-full">
                          {item.name}
                        </span>
                        {subtitle && (
                          <span className="text-xs text-content-primary-subtle truncate max-w-full">
                            {subtitle}
                          </span>
                        )}
                        <EquipmentExpiryPill expiryDate={item.expiryDate} />
                      </button>

                      {isAdmin && (
                        <Button
                          type="button"
                          variant="destructive"
                          size="mini"
                          className="shrink-0"
                          onClick={() => setDeleteTarget(item)}
                          aria-label="Remove equipment"
                        >
                          <Trash2 />
                        </Button>
                      )}
                    </div>
                  )
                })}
              </div>
            )}
          </div>

          {/* Footer */}
          {isAdmin && items.length > 0 && (
            <DrawerFooter>
              <Button
                type="button"
                variant="primary"
                size="sm"
                className="w-full"
                onClick={openCreate}
              >
                <Plus className="size-4" />
                Add Equipment
              </Button>
            </DrawerFooter>
          )}
        </DrawerContent>
      </Drawer>

      {/* Create / edit form (stacked sub-drawer) */}
      <AircraftEquipmentFormDrawer
        aircraftId={aircraftId}
        open={formOpen}
        item={editingItem}
        onClose={() => setFormOpen(false)}
      />

      {/* Delete confirmation */}
      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(isOpen) => !isOpen && setDeleteTarget(null)}
      >
        <AlertDialogContent className="bg-surface-L1">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Equipment?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget?.name} will be removed from this aircraft and no
              longer trigger expiry notifications.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-content-negative-bold hover:bg-content-negative-bold/90"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
